import type { PrefetchContext, PrefetchedDataProps } from 'src/shared/prefetch';

import { useCountryCode, useLocale } from 'src/app/shared/hooks/l10n';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchHomepagePageDataFromApi, useHomepagePageDataFromApi } from './pageData';
import { prefetchHomepagePageFcm, useAsyncHomepagePageFcm } from './pageFcm';
import { prefetchHomepagePageFcmExperiments, useAsyncHomepagePageFcmExperiments } from './pageFcmExperiments';

export type HomepagePagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useHomepagePageDataFromApi>[0];
}>;

export type HomepagePageCombinedState = MergedState<
	ReturnType<typeof useHomepagePageDataFromApi>,
	ReturnType<typeof useAsyncHomepagePageFcmExperiments>,
	ReturnType<typeof useAsyncHomepagePageFcm>
>;

export function usePageContext(prefetchedData?: HomepagePagePrefetchedContext): HomepagePageCombinedState {
	const locale = useLocale();
	const countryCode = useCountryCode();
	return useMergedStates(
		useHomepagePageDataFromApi(prefetchedData?.pageData, { locale, countryCode }),
		useAsyncHomepagePageFcmExperiments(),
		useAsyncHomepagePageFcm(),
	);
}

export async function prefetchPageContext(context: PrefetchContext): Promise<HomepagePagePrefetchedContext> {
	const [pageData] = await Promise.all([
		prefetchHomepagePageDataFromApi(context, {
			countryCode: context.l10n.countryCode,
			locale: context.l10n.locale,
		}),
		prefetchHomepagePageFcmExperiments(context),
		prefetchHomepagePageFcm(context),
	]);

	return { pageData };
}

export type HomepagePagePrefetchedDataProps = PrefetchedDataProps<HomepagePagePrefetchedContext>;
